import { isClosedAccount } from "./closedAccounts.js";

/**
 * The Banks page lists accounts by what they are rather than where they are held: cash, cards,
 * investments, loans. Institutions still exist, and are arranged on their own, but they no longer
 * decide the layout.
 */
export const ACCOUNT_SECTIONS = [
	{ key: 'depository', label: 'Cash' },
	{ key: 'credit', label: 'Credit cards' },
	{ key: 'brokerage', label: 'Investments' },
	{ key: 'loan', label: 'Loans' },
];

const OTHER_SECTION = { key: 'other', label: 'Other' };

/** An account's balance on `date`, signed so a debt is negative, or null when it has none by then. */
export function accountBalanceOn(account, date) {
	const balance = date ? account.getLatestBalanceForMonth(date, true) : account.getLatestBalance();
	return balance ? balance.getRelativeBalanceAmount() : null;
}

/** The credit limit read with the balance, for a card that reported one. */
export function accountLimitOn(account, date) {
	const balance = date ? account.getLatestBalanceForMonth(date, true) : account.getLatestBalance();
	return balance?.limit || null;
}

/**
 * Every open account, in a section per type, each in institution order and then the institution's
 * own order. A section with nothing in it is left out. Closed accounts are never here; they have
 * sections of their own, `closedAccountSections`.
 *
 * A row's `counted` is whether it adds into the totals; the subtotal adds only those, by the same
 * rule as the page's header (`countsInTotals`).
 *
 * @param institutions {PlaidAccount[]}
 * @param date {moment.Moment|null}
 */
export function accountSections(institutions, date) {
	return sectionsOf(institutions, date, (account) => !isClosedAccount(account));
}

/**
 * Every closed account, in sections by type the way the open ones are, for listing apart from them.
 * None of them counts, so these have no subtotal. Empty when nothing is closed.
 *
 * @param institutions {PlaidAccount[]}
 * @param date {moment.Moment|null}
 */
export function closedAccountSections(institutions, date) {
	return sectionsOf(institutions, date, isClosedAccount)
		.map(({ subtotal, ...section }) => section);
}

function sectionsOf(institutions, date, includes) {
	const byKey = {};
	for(const institution of institutions || []) {
		for(const account of institution.accounts || []) {
			if(!includes(account)) {
				continue;
			}
			const section = ACCOUNT_SECTIONS.find((candidate) => candidate.key === account.type) || OTHER_SECTION;
			if(!byKey[section.key]) {
				byKey[section.key] = { ...section, rows: [], subtotal: 0 };
			}
			const balance = accountBalanceOn(account, date);
			const counted = countsInTotals(account);
			byKey[section.key].rows.push({ account, institution, balance, counted });
			if(counted && balance !== null) {
				byKey[section.key].subtotal += balance;
			}
		}
	}
	return [...ACCOUNT_SECTIONS, OTHER_SECTION]
		.map((section) => byKey[section.key])
		.filter(Boolean);
}

/**
 * Whether an account adds into the page's totals: not when it is left out of them, and not when it is
 * closed. A closed account's last balance is whatever it held before closing, and counting it made the
 * totals disagree with the list whenever closed accounts were hidden.
 */
export function countsInTotals(account) {
	return !!account.shouldAddToTotalAssets() && !isClosedAccount(account);
}

/**
 * What the counted accounts hold, what they owe, and the difference, in cents.
 *
 * Debts are given as a positive amount owed.
 */
export function accountTotals(institutions, date) {
	let assets = 0;
	let debts = 0;
	for(const institution of institutions || []) {
		for(const account of institution.accounts || []) {
			if(!countsInTotals(account)) {
				continue;
			}
			const balance = accountBalanceOn(account, date);
			if(balance === null) {
				continue;
			}
			if(account.isLiability()) {
				debts -= balance;
			} else {
				assets += balance;
			}
		}
	}
	return { assets, debts, net: assets - debts };
}
